/**
 * AccountChip — iter-220.1
 *
 * Surfaces the assigned cash account (or revolving-debt funding) on a
 * movement / payment / receivable list row. Complements the existing
 * `AccountMissingBadge` (which is the "+ Asignar cuenta" CTA for rows
 * that LACK an account).
 *
 * Decision tree (returns the first match):
 *   1. `account_id`     → positive grey chip with Wallet icon + name,
 *                          deep-links to `/history?account_id=<id>`
 *                          so the user can see all the movements for
 *                          that account in one click.
 *   2. `source_debt_id` → rose chip with CreditCard icon + debt name.
 *                          History.jsx already renders this inline; we
 *                          centralize it here so Payments rows show it
 *                          too (a TC-funded recurring expense should
 *                          read "Pagado con Visa Gold" on the list).
 *   3. fallthrough      → returns the `missingFallback` slot, which
 *                          each page wires to its own "asignar cuenta"
 *                          affordance (Payments/Receivables open the
 *                          edit dialog; History uses the existing
 *                          AccountMissingBadge popover).
 *
 * Visual style is intentionally NEUTRAL (slate) for the positive case
 * so the chip never competes with the priority pill, amount, or
 * recurring badge. Truncates at max-w-[180px] for long account names.
 */
import { Link } from "react-router-dom";
import { Wallet, CreditCard } from "lucide-react";

export function AccountChip({
  item,
  accounts = [],
  debts = [],
  lang = "es",
  testid,
  missingFallback = null,
}) {
  const isEs = lang === "es";

  // Transfers are intentionally not handled here — History renders a
  // dedicated "src → dst" route line above the tags so a chip would
  // duplicate the info.
  if (item?.type === "transfer") return missingFallback;

  if (item?.account_id) {
    const acct = accounts.find((a) => a.id === item.account_id);
    const name =
      acct?.name ||
      item.account_name_hint ||
      (isEs ? "Cuenta" : "Account");
    return (
      <Link
        to={`/history?account_id=${item.account_id}`}
        data-testid={testid}
        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors max-w-[180px]"
        title={name}
      >
        <Wallet className="h-3 w-3 shrink-0" />
        <span className="truncate">{name}</span>
      </Link>
    );
  }

  const debtId = item?.source_debt_id || item?.debt_id;
  if (debtId) {
    const d = debts.find((x) => x.id === debtId);
    const name = d?.name || (isEs ? "Tarjeta / línea" : "Card / line");
    return (
      <span
        data-testid={testid ? `${testid}-debt` : undefined}
        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900 max-w-[180px]"
        title={name}
      >
        <CreditCard className="h-3 w-3 shrink-0" />
        <span className="truncate">{name}</span>
      </span>
    );
  }

  return missingFallback;
}
